// Drawer presentation: 3-column square grid, caption/filename labels, icon
// actions with tooltips. All mutations come from the shared owner's handlers.
import { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { safeMediaUrl } from "./MediaStrip.jsx";
import { mediaLabels } from "./mediaDisplay.js";
import { pageCountText, usePdfPageCount } from "./pdfPageCount.js";
import { MediaDrawerViewer } from "./MediaDrawerViewer.jsx";
import { Icon } from "./mediaIcons.jsx";
import { MediaReorderTile } from "./MediaReorderTile.jsx";

export const DRAWER_MEDIA_CSS = `
.pm-tile { position: relative; min-width: 0; border: 1px solid ${T.border}; border-radius: 10px; overflow: hidden; background: ${T.card}; }
.pm-tile:focus-within { box-shadow: 0 0 0 2px ${T.blue}; }
.pm-open { display: block; width: 100%; padding: 0; border: 0; background: ${T.lightGray}; cursor: zoom-in; text-align: left; }
.pm-open:focus-visible { outline: none; }
.pm-square { position: relative; width: 100%; aspect-ratio: 1 / 1; overflow: hidden; }
.pm-acts { position: absolute; right: 6px; top: 6px; display: flex; flex-wrap: wrap; justify-content: flex-end; max-width: calc(100% - 12px); gap: 4px; opacity: 0; transition: opacity .12s; z-index: 2; }
.pm-tile:hover .pm-acts, .pm-tile:focus-within .pm-acts { opacity: 1; }
.pm-icon { position: relative; width: 28px; height: 28px; flex-shrink: 0; border-radius: 7px; border: 0; background: ${T.card}; color: ${T.dark}; display: inline-flex; align-items: center; justify-content: center; box-shadow: ${T.shadowButton}; cursor: pointer; padding: 0; text-decoration: none; }
.pm-icon[data-danger="true"] { color: ${T.red}; }
.pm-icon:disabled { opacity: .5; cursor: wait; }
.pm-icon:focus-visible { outline: 2px solid ${T.blue}; outline-offset: 1px; }
.pm-tip { position: absolute; top: 32px; right: 0; white-space: nowrap; background: ${T.tooltip.bg}; color: ${T.tooltip.fg}; font-size: ${T.fsXs}px; font-weight: ${T.fwSemibold}; padding: 2px 7px; border-radius: 5px; opacity: 0; pointer-events: none; transition: opacity .1s; }
.pm-icon:hover .pm-tip, .pm-icon:focus-visible .pm-tip { opacity: 1; }
.pm-icon:first-child .pm-tip { left: 0; right: auto; }
.pm-cap { padding: 7px 9px; font-size: ${T.fsSm}px; color: ${T.dark}; }
.pm-cap > span { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-cap small { display: block; color: ${T.mid}; font-size: ${T.fsXs}px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-badge { position: absolute; left: 8px; top: 8px; z-index: 1; background: ${T.coverBadgeBg}; color: ${T.white}; font-size: ${T.fsXxs}px; font-weight: ${T.fwBold}; border-radius: 999px; padding: 1px 8px; pointer-events: none; }
.pm-badge[data-auto="true"] { background: ${T.mid}; font-weight: ${T.fwSemibold}; }
@media (hover: none), (pointer: coarse) { .pm-acts { opacity: 1; } .pm-icon { width: 34px; height: 34px; } }
`;

function IconAction({ label, icon, onClick, href, danger, disabled, filled, testId }) {
  const content = <><Icon name={icon} filled={filled} /><span className="pm-tip" aria-hidden="true">{label}</span></>;
  const stop = event => event.stopPropagation();
  if (href) return <a className="pm-icon" href={href} download aria-label={label} title={label} data-testid={testId} onClick={stop}>{content}</a>;
  return <button type="button" className="pm-icon" aria-label={label} title={label} data-danger={danger || undefined} disabled={disabled} data-testid={testId} onClick={event => { stop(event); onClick(event.currentTarget); }}>{content}</button>;
}

export function MediaThumb({ item, size = null }) {
  const [broken, setBroken] = useState(false);
  const src = safeMediaUrl(item.url);
  if (item.type !== "image") return null;
  if (!src || broken) return <div role="img" aria-label={`${mediaLabels(item).primary} (image unavailable)`} style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: T.mid, fontSize: T.fsXs, background: T.lightGray }}>Unavailable</div>;
  return <img draggable={false} src={src} alt={mediaLabels(item).primary} loading="lazy" decoding="async" width={size || undefined} height={size || undefined} onError={() => setBroken(true)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
}

// Starts the page-count read only once the tile scrolls into view.
function useVisible(ref) {
  const [visible, setVisible] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const io = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) setVisible(true); });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, visible]);
  return visible;
}

export function PdfTileFace({ item, compact = false }) {
  const ref = useRef(null);
  const visible = useVisible(ref);
  const pages = usePdfPageCount(item, visible);
  return <div ref={ref} data-testid={`media-pdf-${item.id}`} style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, background: T.lightGray, color: T.red, fontWeight: T.fwBold }}>
    <Icon name="doc" size={compact ? 22 : 30} />
    <span style={{ fontSize: compact ? T.fsXxs : T.fsSm }}>PDF</span>
    {!compact && <small data-testid={`media-pages-${item.id}`} style={{ color: T.mid, fontWeight: T.fwSemibold, fontSize: T.fsXs }}>{pageCountText(pages)}</small>}
  </div>;
}

export function MediaDrawerGrid({ items, cover, canManage, busy, hidden, viewerHost, onSetCover, onClearCover, onCaption, onRemove, onReorder }) {
  const [activeId, setActiveId] = useState(null);
  useEffect(() => { if (hidden) setActiveId(null); }, [hidden]);
  const images = useMemo(() => items.filter(item => item.type === "image"), [items]);
  const active = items.find(item => String(item.id) === String(activeId));
  const viewerItems = active?.type === "pdf" ? [active] : images;
  return <>
    <style>{DRAWER_MEDIA_CSS}</style>
    <div data-testid="media-drawer-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
      {items.map(item => {
        const labels = mediaLabels(item);
        const isCover = cover.id != null && String(cover.id) === String(item.id) && item.type === "image";
        const explicit = isCover && cover.explicit;
        const download = safeMediaUrl(item.downloadUrl || item.url);
        return <MediaReorderTile key={item.id} item={item} items={items} coverId={cover.id} canReorder={canManage} busy={busy} onReorder={onReorder} className="pm-tile" data-testid={`media-item-${item.id}`} data-type={item.type}>
          <button type="button" className="pm-open" onClick={() => setActiveId(item.id)} aria-label={item.type === "image" ? `Open image: ${labels.primary}` : `Open document: ${labels.primary}`}>
            <div className="pm-square">
              {item.type === "image" ? <MediaThumb item={item} /> : <PdfTileFace item={item} />}
            </div>
          </button>
          {isCover && <span className="pm-badge" data-auto={!explicit || undefined} data-testid={explicit ? `media-cover-badge-${item.id}` : `media-auto-cover-${item.id}`} title={explicit ? "Selected cover" : "Automatic cover (first image)"}>Cover</span>}
          <div className="pm-acts">
            {canManage && item.type === "image" && (explicit
              ? <IconAction icon="star" filled label="Clear cover" testId={`media-clear-cover-${item.id}`} disabled={busy} onClick={() => onClearCover(item)} />
              : <IconAction icon="star" label="Set as cover" testId={`media-set-cover-${item.id}`} disabled={busy} onClick={() => onSetCover(item)} />)}
            {canManage && <IconAction icon="pencil" label="Edit caption" testId={`media-caption-${item.id}`} disabled={busy} onClick={() => onCaption(item)} />}
            {download && <IconAction icon="download" label="Download" href={download} testId={`media-download-${item.id}`} />}
            {canManage && <IconAction icon="trash" danger label="Remove" testId={`media-remove-${item.id}`} disabled={busy} onClick={trigger => onRemove(item, trigger)} />}
          </div>
          <div className="pm-cap" title={labels.filename || labels.primary}>
            <span data-testid={`media-label-${item.id}`}>{labels.primary}</span>
            {labels.secondary && <small>{labels.secondary}</small>}
          </div>
        </MediaReorderTile>;
      })}
    </div>
    {!hidden && <MediaDrawerViewer items={viewerItems} activeId={activeId} onIndex={setActiveId} onClose={() => setActiveId(null)} host={viewerHost} />}
  </>;
}
